import { collectSubjectKeys } from "./timezone";
import { getBoundarySubjectMeta } from "../data/subjectCatalog";

export const BOUNDARY_OPTIONS = [
  { value: "m26", label: "May 2026 (m26)", timezoneMode: "triple" },
  { value: "m25", label: "May 2025 (m25)", timezoneMode: "triple" },
  { value: "n25", label: "November 2025 (n25)", timezoneMode: "triple" },
  { value: "m24", label: "May 2024 (m24)", timezoneMode: "double" },
  { value: "n24", label: "November 2024 (n24)", timezoneMode: "double" },
  { value: "m23", label: "May 2023 (m23)", timezoneMode: "double" },
  { value: "n23", label: "November 2023 (n23)", timezoneMode: "double" },
  { value: "m22", label: "May 2022 (m22)", timezoneMode: "double" },
  { value: "n22", label: "November 2022 (n22)", timezoneMode: "single" },
  { value: "m21", label: "May 2021 (m21)", timezoneMode: "double" },
  { value: "n20", label: "November 2020 (n20)", timezoneMode: "single" },
  { value: "n19", label: "November 2019 (n19)", timezoneMode: "single" },
  { value: "m19", label: "May 2019 (m19)", timezoneMode: "double" },
];

export const KNOWN_BOUNDARIES = BOUNDARY_OPTIONS.map((boundary) => boundary.value);

// The most recent session. BOUNDARY_OPTIONS is newest-first, so adding a session
// at the top of that list is the only edit needed to roll the app forward.
export const LATEST_BOUNDARY = BOUNDARY_OPTIONS[0].value;
export const SCORE_FALLBACK_BOUNDARIES = [
  "m24",
  "n24",
  "m23",
  "n23",
  "m22",
  "n22",
  "m21",
  "n20",
  "n19",
  "m19",
];

const TZ_OPTIONS_BY_MODE = {
  single: ["tz1"],
  double: ["tz1", "tz2"],
  triple: ["tz1", "tz2", "tz3"],
};

const BOUNDARY_TZ_MODE_LOOKUP = Object.fromEntries(
  BOUNDARY_OPTIONS.map(({ value, timezoneMode }) => [value, timezoneMode])
);

export const getTimezoneOptionsForBoundary = (boundary) => {
  const mode = BOUNDARY_TZ_MODE_LOOKUP[boundary] || "double";
  return TZ_OPTIONS_BY_MODE[mode] || TZ_OPTIONS_BY_MODE.double;
};

const uniqueBoundaries = (boundaries) =>
  Array.from(new Set(boundaries.filter(Boolean)));

const getBoundarySortValue = (boundary) => {
  const match = String(boundary || "").match(/^([mn])(\d{2})$/i);
  if (!match) return Number.NEGATIVE_INFINITY;

  const sessionOffset = match[1].toLowerCase() === "m" ? 0 : 1;
  return Number(match[2]) * 2 + sessionOffset;
};

export const getBoundaryResolutionOrder = (selectedBoundary) => {
  const selectedSortValue = getBoundarySortValue(selectedBoundary);
  const remainingKnown = KNOWN_BOUNDARIES.filter(
    (boundary) => boundary !== selectedBoundary && boundary !== "m24"
  ).sort((left, right) => {
    const leftDistance = Math.abs(getBoundarySortValue(left) - selectedSortValue);
    const rightDistance = Math.abs(getBoundarySortValue(right) - selectedSortValue);

    if (leftDistance !== rightDistance) {
      return leftDistance - rightDistance;
    }

    return getBoundarySortValue(right) - getBoundarySortValue(left);
  });

  return uniqueBoundaries([selectedBoundary, "m24", ...remainingKnown]);
};

export const getBoundaryCatalogOrder = (selectedBoundary, includeAllBoundaries = false) =>
  includeAllBoundaries
    ? uniqueBoundaries(KNOWN_BOUNDARIES)
    : uniqueBoundaries([selectedBoundary, "m24"]);

export const collectUniqueSubjectKeysFromDatasets = (dataByBoundary, boundaries) => {
  const keys = new Set();
  boundaries.forEach((boundary) => {
    const payload = dataByBoundary?.[boundary];
    if (!payload?.timezones) return;
    collectSubjectKeys(payload.timezones).forEach((key) => keys.add(key));
  });
  return Array.from(keys);
};

export const collectCatalogEntriesFromDatasets = (dataByBoundary, boundaries) => {
  const uniqueKeys = collectUniqueSubjectKeysFromDatasets(dataByBoundary, boundaries);
  return uniqueKeys
    .map((subjectKey) => getBoundarySubjectMeta(subjectKey))
    .filter(Boolean);
};
